import React, { useState, useCallback } from 'react';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Button, TextInput } from 'react-scale/components/design-system';
import { parseAnythingSeparatedIds } from '@scale/shared/utils';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { makeStyles } from 'design-system/styles';
import { IUserJson } from '@scale/shared/types/user';
import { SensitiveContentTagNames } from '@scale/shared/corp/sensitiveContentUtilsConstants';

const description = `
### Description: 

This tool lets you remove workers from **all** sensitive content projects they may be a part of. Note that if you only intend to remove tasker(s) from specific sensitive content projects, just remove them from those projects' worker teams and manually disable them for those projects.

Specific functionality includes:
- Removes the *${SensitiveContentTagNames.SecureConsentToCounseling}* and *${SensitiveContentTagNames.SecureSCTag}* tags from that worker
- Adds the *${SensitiveContentTagNames.SecureNoSCTag}* tag to that worker
- Executing the funnels of the worker team that that the user(s) are on to move them off of the team
  - This will move workers to either the "Free Agent/Misc" team or the override team specified when onboarding the project as sensitive content
- Remove worker permissions from sensitive content projects (to remove them from relevant Slack channels)

#### Author: Nikash Sethi
`;

const OnboardProjectsAction: React.FC<{ user: IUserJson }> = () => {
  const classes = useStyles();

  const [workersString, setWorkersString] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleOnboardProjects = useCallback(async () => {
    setLoading(true);
    const workerIds = parseAnythingSeparatedIds(workersString);
    try {
      const { message: responseMessage, error } = await apiFetch(
        null,
        `corp-api/sensitive-content-utils/remove-sc-workers/`,
        {
          method: 'POST',
          body: {
            workerIds,
          },
        },
      );
      if (error) {
        setErrorMessage(error);
        setMessage('');
      } else if (responseMessage) {
        setMessage(responseMessage);
        setErrorMessage('');
      }
    } catch (error) {
      setErrorMessage(JSON.stringify(error));
      setMessage('');
    }
    setLoading(false);
  }, [workersString]);

  return (
    <div>
      <SanitizedMarkdown text={description} />
      <TextInput
        className={classes.marginTop}
        value={workersString}
        label="Worker IDs (anything separated)"
        fullWidth
        multiline
        required
        size="large"
        onChange={e => setWorkersString(e.target.value)}
        disabled={loading}
      ></TextInput>
      <Button
        className={classes.marginTop}
        type="primary"
        onClick={handleOnboardProjects}
        disabled={loading || !workersString.length}
      >
        Submit
      </Button>
      {message.length ? <pre>{message}</pre> : <></>}
      {errorMessage.length ? <pre style={{ color: 'red' }}>{errorMessage}</pre> : <></>}
    </div>
  );
};

const useStyles = makeStyles({
  marginTop: {
    marginTop: 20,
  },
});

export default OnboardProjectsAction;
